
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';
import GoogleMap from './GoogleMap';

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  // Extract Google Maps URL if present in the content
  let mapLocation = '';
  
  // Check if there's a Google Maps URL in the content (typically in a link)
  const mapUrlMatch = content.match(/https:\/\/www\.google\.com\/maps\/search\/([^)]+)/);
  if (mapUrlMatch && mapUrlMatch[1]) {
    // URL decode the location from the map URL
    mapLocation = decodeURIComponent(mapUrlMatch[1].replace(/\+/g, ' '));
  }

  return (
    <div className="markdown-renderer prose prose-sm max-w-none dark:prose-invert">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Style headings
          h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-4" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-3" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-md font-bold mb-2" {...props} />,
          
          // Style links to open in new tab
          a: ({ node, children, href, ...props }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[hsl(var(--link))] hover:text-[hsl(var(--link)/0.8)] hover:underline inline-flex items-center"
              {...props}
            >
              {children}
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          ),
          
          // Style tables
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border-collapse border border-border rounded-md" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-muted/50" {...props} />,
          th: ({ node, ...props }) => <th className="border border-border px-3 py-2 text-left font-medium text-foreground" {...props} />,
          td: ({ node, ...props }) => <td className="border border-border px-3 py-2 text-muted-foreground" {...props} />,
          
          // Style lists
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-4" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-4" {...props} />,
          
          // Style paragraphs
          p: ({ node, ...props }) => <p className="mb-4" {...props} />,
          
          // Style blockquotes
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-muted-foreground pl-4 italic" {...props} />
          ),
          
          // Style strong/bold text
          strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
      
      {/* If we found a map location, render the map */}
      {mapLocation && (
        <div className="mt-6">
          <h3 className="text-md font-bold mb-2">Location</h3>
          <GoogleMap location={mapLocation} />
        </div>
      )}
    </div>
  );
};

export default MarkdownRenderer;
